import React from 'react';
import Button from '../../components/elements/Button';
import { H1 } from '../../components/elements/Text';
import Navbar from '../../components/Navbar';
import PageWithAsyncData from '../../components/PageWithAsyncData';
import PageWrapper from '../../components/PageWrapper';
import styled from 'styled-components';
import { useGetCompanies } from '../../hooks/use-hydra/admin';
import {
  createColumnHelper,
  flexRender,
  useReactTable,
} from '@tanstack/react-table';
import { getCoreRowModel } from '@tanstack/react-table';
import { CompaniesResponse } from '../../api/admin';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  ClickableTableRow,
} from '../../components/Table';
import { TableWrapper } from '../../components/Table';
import { useHistory } from 'react-router-dom';
import day from 'dayjs';
import utc from 'dayjs/plugin/utc';
import AddCompanyModal from '../../components/Admin/Modals/AddCompanyModal';
day.extend(utc);

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

type Company = CompaniesResponse['items'][number];

const AddCompanyButton = ({ onClick }: { onClick: () => void }) => (
  <Button
    label="Add Company"
    type="secondary"
    faIcon="fa-solid fa-plus"
    showIconBeforeLabel
    onClick={onClick}
  />
);

const Header = () => (
  <Navbar
    breadCrumbs={[
      {
        label: 'Home',
        url: '/',
      },
      {
        label: 'Companies',
        url: null,
      },
    ]}
  />
);

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  margin-bottom: 72px;
`;

export default function Companies() {
  const { data, isLoading, error, refetch } = useGetCompanies();
  const history = useHistory();
  const [isAddCompanyModalOpen, setIsAddCompanyModalOpen] =
    React.useState(false);

  const onGoToCompany = (id: string) => {
    history.push(`/admin/companies/${id}`);
  };

  return (
    <PageWithAsyncData isLoading={isLoading} error={error} retry={refetch}>
      <Header />
      <PageWrapper>
        <H1>Companies</H1>
        <Content>
          <Row>
            <AddCompanyButton onClick={() => setIsAddCompanyModalOpen(true)} />
          </Row>
          <CompaniesTable
            data={data?.items ?? []}
            onGoToCompany={onGoToCompany}
          />
        </Content>
      </PageWrapper>
      <AddCompanyModal
        isOpen={isAddCompanyModalOpen}
        onClose={() => setIsAddCompanyModalOpen(false)}
      />
    </PageWithAsyncData>
  );
}

const columnHelper = createColumnHelper<Company>();

const columns = [
  columnHelper.accessor('id', {
    header: 'ID',
  }),
  columnHelper.accessor('name', {
    header: 'Name',
  }),
  columnHelper.display({
    id: 'latestCohort',
    header: 'Latest Cohort',
    cell: ({ row }) => {
      return `${
        row.original.latestCohortStartDate && row.original.latestCohortEndDate
          ? day.utc(row.original.latestCohortStartDate).format('DD/MM/YYYY') +
            ' - ' +
            day.utc(row.original.latestCohortEndDate).format('DD/MM/YYYY')
          : '-'
      }`;
    },
  }),
  columnHelper.accessor('numLearners', {
    header: 'Num of Learners',
  }),
];

const CompaniesTable = ({
  data,
  onGoToCompany,
}: {
  data: Company[];
  onGoToCompany: (id: string) => void;
}) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <TableWrapper>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <ClickableTableRow
              key={row.id}
              onClick={() => onGoToCompany(row.original.id.toString())}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </ClickableTableRow>
          ))}
        </TableBody>
      </Table>
    </TableWrapper>
  );
};
